import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api, { resolveBackendUrl } from '../../services/api';

function LogoConfig({ tenantId }) {
  const [acronym, setAcronym] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/logo/${tenantId}`);
      if (response.data.success && response.data.data) {
        setExisting(response.data.data);
        setAcronym(response.data.data.Acronym || '');
      } else {
        setExisting(null);
        setAcronym('');
      }
      setFile(null);
      setPreview(null);
    } catch {
      toast.error('Failed to load logo configuration');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > 500 * 1024) {
      toast.error('File size must be less than 500KB');
      e.target.value = '';
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleSubmit = async () => {
    if (!acronym.trim()) {
      toast.error('Acronym is required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('tenantId', tenantId);
      formData.append('acronym', acronym.trim());
      if (file) {
        formData.append('logo', file);
      }

      const response = await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        toast.success(response.data.message);
        loadConfig();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save logo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>;

  return (
    <div>
      <div className="logo-upload-section">
        <div className="logo-preview-area">
          <h4>Logo Preview</h4>
          <div className="logo-preview-box">
            {preview ? (
              <img src={preview} alt="Logo preview" className="logo-preview-img" />
            ) : (existing?.LogoImageUrl || existing?.LogoUrl) ? (
              <img
                src={resolveBackendUrl(existing.LogoImageUrl) || existing.LogoUrl}
                alt="Current logo"
                className="logo-preview-img"
              />
            ) : (
              <div className="logo-placeholder">
                <span>No Logo</span>
              </div>
            )}
          </div>
          {existing?.FileName && !file && (
            <p className="logo-file-info">Current: {existing.FileName} ({(existing.FileSize / 1024).toFixed(1)} KB)</p>
          )}
        </div>

        <div className="logo-form-area">
          <div className="form-group">
            <label className="required-label">Institute Acronym</label>
            <input
              type="text"
              placeholder="e.g., MIT, IITB, NIT"
              value={acronym}
              onChange={(e) => setAcronym(e.target.value.toUpperCase())}
              maxLength={20}
            />
          </div>

          <div className="form-group">
            <label>Upload Logo</label>
            <div className="file-upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                id="logo-upload"
                className="file-input"
              />
              <label htmlFor="logo-upload" className="file-upload-label">
                <span className="file-upload-icon">📁</span>
                <span>{file ? file.name : 'Choose file (Max 500KB)'}</span>
              </label>
            </div>
            <span className="form-hint">Accepted formats: PNG, JPG, SVG. Max size: 500KB</span>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button className="btn btn-submit" onClick={handleSubmit} disabled={saving}>
          <span>💾</span> {saving ? 'Saving...' : 'Save Logo'}
        </button>
        <button className="btn btn-cancel" onClick={loadConfig}>
          <span>✕</span> Cancel
        </button>
      </div>
    </div>
  );
}

export default LogoConfig;
